import { useEffect, useRef, useState } from 'react';
import {
  Bell,
  ChevronDown,
  CircleHelp,
  CreditCard,
  FileText,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  PackageCheck,
  ReceiptText,
  UserRound,
  X,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { Avatar } from '@/components/ui/Avatar';
import { ChangePasswordDialog } from '@/features/auth/ChangePasswordDialog';
import { NotificationBell } from '@/features/notifications/NotificationBell';

export function PortalLayout() {
  const { user, can, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [menuOpen]);

  if (!user) return null;

  const displayName = user.fullName || 'Vendor';
  const closeSidebar = () => setSidebarOpen(false);
  const signOut = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/portal/login', { replace: true });
  };

  return <div className="portal-shell app-shell">
    {sidebarOpen && <button className="sidebar-scrim" onClick={closeSidebar} aria-label="Close navigation" />}
    <aside className={`sidebar portal-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
      <div className="brand">
        <NavLink className="brand__home" end to="/portal" onClick={closeSidebar} aria-label="Go to vendor dashboard">
          <span className="brand__mark">V</span>
          <div><strong>Vendiz</strong><small>Vendor portal</small></div>
        </NavLink>
        <button className="mobile-close" type="button" onClick={closeSidebar} aria-label="Close navigation"><X /></button>
      </div>

      <nav>
        <div className="nav-group">
          <span className="nav-group__label">Workspace</span>
          <PortalNavLink end to="/portal" label="Dashboard" icon={LayoutDashboard} onClick={closeSidebar} />
          {can('PORTAL.PO.VIEW') && <PortalNavLink to="/portal/purchase-orders" label="Purchase orders" icon={FileText} onClick={closeSidebar} />}
          <PortalNavLink to="/portal/goods-receipts" label="Goods receipts" icon={PackageCheck} onClick={closeSidebar} />
          <PortalNavLink to="/portal/notifications" label="Notifications" icon={Bell} onClick={closeSidebar} />
        </div>
        <div className="nav-group">
          <span className="nav-group__label">Finance</span>
          {can('PORTAL.INVOICE.UPLOAD') && <PortalNavLink to="/portal/invoices" label="Invoices" icon={ReceiptText} onClick={closeSidebar} />}
          {can('PORTAL.PAYMENT.VIEW') && <PortalNavLink to="/portal/payments" label="Payments" icon={CreditCard} onClick={closeSidebar} />}
        </div>
      </nav>

      <div className="sidebar-footer">
        <PortalNavLink to="/portal/help" label="Help & support" icon={CircleHelp} onClick={closeSidebar} />
      </div>
    </aside>

    <div className="app-main portal-main">
      <header className="topbar portal-topbar">
        <button className="menu-button" type="button" onClick={() => setSidebarOpen(true)} aria-label="Open navigation"><Menu /></button>
        <div className="topbar-actions">
          <NotificationBell />
          <span className="topbar-divider" />
          <div className="profile-menu" ref={menuRef}>
            <button className="profile-trigger" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen} aria-haspopup="menu">
              <Avatar initials={initials(displayName)} name={displayName} />
              <span className="user-copy"><strong>{displayName}</strong><span>Vendor</span></span>
              <ChevronDown size={16} className={menuOpen ? 'is-open' : ''} />
            </button>
            {menuOpen && <div className="profile-dropdown" role="menu">
              <div className="profile-dropdown__identity"><strong>{displayName}</strong><span>Vendor portal</span></div>
              <NavLink to="/portal/profile" role="menuitem" onClick={() => setMenuOpen(false)}><UserRound size={17} /> Profile</NavLink>
              <button role="menuitem" onClick={() => { setMenuOpen(false); setPasswordOpen(true); }}><KeyRound size={17} /> Change password</button>
              <button role="menuitem" onClick={() => void signOut()}><LogOut size={17} /> Log out</button>
            </div>}
          </div>
        </div>
      </header>
      <main className="content portal-content"><Outlet /></main>
    </div>
    <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
  </div>;
}

function PortalNavLink({ to, label, icon: Icon, end, onClick }: { to: string; label: string; icon: typeof Bell; end?: boolean; onClick: () => void }) {
  return <NavLink end={end} to={to} onClick={onClick} className={({ isActive }) => isActive ? 'active' : ''}><Icon size={18} /><span>{label}</span></NavLink>;
}

function initials(value: string) {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}
